// Companion/blocks/DraftQuestionBlock.tsx
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ThumbsDown } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type {
  ChatDraftQuestionBlockPayload,
  ChatDraftQuestionReadyPayload,
} from '@/types/chatBlocks';
import {
  answerChatDraftQuestion,
  fetchChatDraftQuestion,
  generateChatDraftQuestion,
  sendChatDraftQuestionFeedback,
  CHAT_DRAFT_POLL_INTERVAL_MS,
  CHAT_DRAFT_POLL_WINDOW_MS,
  type ChatDraftFeedbackReason,
} from '@/services/chatDrafts';
import { parseDraftQuestionBlockPayload } from './chatBlockPayload';
import { useIsChatBlockHidden } from './chatBlockVisibility';
import { draftTextParagraphs, renderDraftText } from './draftQuestionText';

/**
 * A practice question Ora wrote itself, served as a labelled draft (ORA-934).
 *
 * It is the vetted question block's layout — same `chat-ui_question` and
 * `chat-ui_explanation` classes, same commit-then-reveal, same "correct option
 * first" explanation panel — over none of its machinery. The differences are
 * all deliberate, and each one is a rule from the design rather than a
 * shortcut:
 *
 * - **Text, never trusted HTML.** The stem, the options, the explanations and
 *   the objective were written by a model inside a server tool, so they render
 *   through `renderDraftText` (a two-tag `<sup>`/`<sub>` tokenizer), never
 *   through `renderVignetteContent`. See `draftQuestionText.tsx`.
 * - **Nothing is scored.** The pick posts to the draft's own endpoint, which
 *   records the letter and stops there: no QBank response row, no allowance
 *   spent, no scheduling, so no `limits` line and no upgrade modal either.
 * - **No study affordances.** No explainer popovers (the text has no explainer
 *   spans and a model's prose must not be scanned for them), no highlights, no
 *   crossouts, no flag, no report, no linked-flashcard or scheduled-variant
 *   badges — all of those address a library item, and a draft is not one.
 * - **No anchor.** A draft has no `vignetteId`, so it never claims the thread's
 *   question anchor and the hidden-answer guard never sees it; the reveal is
 *   the block's own, off the payload.
 *
 * What it adds is the labelling, the thumbs-down, and the wait: a student has
 * to be able to tell a draft from a vetted question at a glance, to say what was
 * wrong with one — and to watch one being written. Writing takes longer than a
 * turn may last, so the turn serves a `pending` block and THIS COMPONENT asks
 * the server to generate (`DraftQuestionGate` below), rather than the tool
 * holding the turn open. A reload mid-write arrives pending again and waits the
 * same way; a reload after it finished arrives ready and renders at once.
 */

/** The four reasons a draft can be marked down, with the enum each posts. */
const FEEDBACK_REASONS: ReadonlyArray<{
  reason: ChatDraftFeedbackReason;
  label: string;
}> = [
  { reason: 'WRONG_ANSWER', label: 'Wrong answer' },
  { reason: 'TWO_RIGHT_ANSWERS', label: 'Two right answers' },
  { reason: 'NOT_FROM_MY_SOURCE', label: 'Not from my lecture' },
  { reason: 'UNCLEAR_STEM', label: 'Unclear stem' },
];

/**
 * One model-written passage as paragraphs, with `<sup>`/`<sub>` honoured.
 *
 * Blank lines are the paragraph break; a SINGLE newline is a line break the
 * author meant (a lab list, a vitals line), so `whitespace-pre-line` keeps it
 * — the same thing `white-space: pre-line` does for the vetted explanations in
 * the exam panel.
 */
const DraftProse = ({ text }: { text: string }) => (
  <>
    {draftTextParagraphs(text).map((paragraph, index) => (
      <p className="whitespace-pre-line" key={index}>
        {renderDraftText(paragraph)}
      </p>
    ))}
  </>
);

interface ReadyDraftQuestionProps {
  payload: ChatDraftQuestionReadyPayload;
  isReadOnly: boolean;
  onBeforeContentExpand?: () => void;
}

/** The question itself, once there is one. */
const ReadyDraftQuestion = ({
  payload,
  isReadOnly,
  onBeforeContentExpand,
}: ReadyDraftQuestionProps) => {
  const [selected, setSelected] = useState<string | null>(
    payload.selectedLetter?.trim() || null
  );

  const [feedbackReason, setFeedbackReason] = useState<string | null>(
    payload.feedbackReason?.trim() || null
  );

  const [isChoosingReason, setIsChoosingReason] = useState(false);
  const { correctLetter } = payload;

  const explanationByLetter = new Map(
    payload.explanations.map((explanation) => [
      explanation.letter,
      explanation.text,
    ])
  );

  /**
   * The explanation panel's rows: the correct option first, the rest in the
   * order they are on screen — the vetted panel's order, which the FE pins in
   * `ExplanationDisplay.test.tsx` and chat mirrors in `QuestionBlockBody`.
   */
  const explainedOptions = [
    ...payload.options.filter((option) => option.letter === correctLetter),
    ...payload.options.filter((option) => option.letter !== correctLetter),
  ]
    .map((option) => ({
      ...option,
      isCorrect: option.letter === correctLetter,
      explanation: explanationByLetter.get(option.letter) ?? '',
    }))
    // A distractor with nothing to say is dropped; the correct option never is.
    // "Which one is right" is the panel's whole point, so it keeps its heading
    // even when the generator wrote no prose under it.
    .filter((option) => option.isCorrect || option.explanation !== '');

  const pick = (letter: string) => {
    // One pick per draft, and a restored legacy thread is an archive.
    if (isReadOnly || selected) return;

    // Revealed from the payload, before the write — the same optimistic reveal
    // the vetted block does. A draft is not scored, so a failed write has
    // nothing to roll back and says nothing to the student. The explanation
    // panel is about to appear, so the thread keeps the student's position
    // across it first, exactly as the vetted block does.
    onBeforeContentExpand?.();
    setSelected(letter);
    void answerChatDraftQuestion(payload.draftId, letter);
  };

  const sendFeedback = (reason: ChatDraftFeedbackReason) => {
    if (isReadOnly || feedbackReason) return;

    setFeedbackReason(reason);
    setIsChoosingReason(false);
    void sendChatDraftQuestionFeedback(payload.draftId, reason);
  };

  return (
    <div className="chat-ui_question">
      <div className="mb-1 flex flex-col items-start gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="warning">Not vetted</Badge>
          {payload.questionType ? (
            // The kicker's own type scale (`text-2xs` is the token for the
            // 10px the header's kicker uses): this reads as part of the card's
            // label row ("Practice Question · AI draft" above it), not as
            // content.
            <span className="text-2xs font-medium tracking-wide text-fg-secondary uppercase">
              {payload.questionType}
            </span>
          ) : null}
        </div>
        <p className="m-0 text-xs text-fg-secondary">
          Written by Ora from {payload.sourceLabel}. Not reviewed by Ora&apos;s
          physicians. Check key facts.
        </p>
      </div>

      <div className="chat-ui_question-stem prose-ora prose-ora-study">
        <DraftProse text={payload.stem} />
      </div>

      <div className="chat-ui_question-options">
        {payload.options.map((option) => {
          const { letter } = option;
          const isSelected = selected === letter;
          const isRevealed = Boolean(selected);
          const isCorrectAnswer = isRevealed && letter === correctLetter;

          const isIncorrectAttempt =
            isRevealed && isSelected && letter !== correctLetter;

          return (
            <div className="chat-ui_question-option-row" key={letter}>
              <button
                className={`chat-ui_question-option${
                  isSelected ? ' is-selected' : ''
                }${isIncorrectAttempt ? ' is-incorrect-attempt' : ''}${
                  isCorrectAnswer ? ' is-correct-answer' : ''
                }`}
                data-option-letter={letter}
                // `aria-disabled`, not the native attribute, so the answered
                // option keeps the vetted block's look (the stylesheet styles
                // `[aria-disabled='true']`) and stays readable to a screen
                // reader. `pick` is what actually refuses a second answer.
                aria-disabled={isRevealed || undefined}
                onClick={() => pick(letter)}
                type="button"
              >
                <span className="chat-ui_question-option-label">
                  <strong>{letter}.</strong>
                  <span>{renderDraftText(option.text)}</span>
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {selected ? (
        <div className="chat-ui_explanation">
          <strong>Correct: {correctLetter}</strong>
          {explainedOptions.map((option) => (
            <div
              className={`chat-ui_explanation-answer${
                option.isCorrect ? ' is-correct' : ''
              }${option.letter === selected ? ' user-answer' : ''}`}
              key={option.letter}
            >
              <p className="chat-ui_explanation-answer-option">
                <strong>{option.letter}. </strong>
                {renderDraftText(option.text)}
              </p>
              <div className="chat-ui_explanation-answer-body prose-ora prose-ora-study">
                <DraftProse text={option.explanation} />
              </div>
            </div>
          ))}
          {payload.learningObjective ? (
            <p className="mt-3">
              <strong>Learning Objective: </strong>
              {renderDraftText(payload.learningObjective)}
            </p>
          ) : null}
          {isReadOnly ? null : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {feedbackReason ? (
                <span className="text-xs text-fg-secondary">
                  Thanks — we&apos;ll use that to improve these.
                </span>
              ) : isChoosingReason ? (
                <>
                  <span className="text-xs text-fg-secondary">
                    What was wrong?
                  </span>
                  {FEEDBACK_REASONS.map(({ reason, label }) => (
                    <Button
                      key={reason}
                      onClick={() => sendFeedback(reason)}
                      size="xs"
                      type="button"
                      variant="outline"
                    >
                      {label}
                    </Button>
                  ))}
                </>
              ) : (
                <Button
                  aria-label="Something is wrong with this question"
                  onClick={() => setIsChoosingReason(true)}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <ThumbsDown aria-hidden="true" />
                </Button>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

/** The label row every state draws: the badge, and whatever sits beside it. */
const DraftLabelRow = ({ children }: { children?: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Badge variant="warning">Not vetted</Badge>
    {children}
  </div>
);

/** Waiting for the question, and what the student sees if it never arrives. */
const DraftStatusView = ({
  sourceLabel,
  isPending,
}: {
  sourceLabel: string;
  isPending: boolean;
}) => (
  // The height is reserved on purpose. A question lands in place of this line,
  // and a thread that jumps a screenful at the moment it arrives is a thread
  // the student loses their place in; `min-h-48` is about what a served
  // question's stem and five options occupy, so the answer below barely moves.
  <div className="flex min-h-48 flex-col items-start gap-1.5">
    <DraftLabelRow />
    <p
      aria-live="polite"
      className="m-0 flex items-center gap-2 text-xs text-fg-secondary"
    >
      {isPending ? (
        <>
          <Spinner size={14} thickness={3} />
          Writing your question from {sourceLabel}…
        </>
      ) : (
        "Ora couldn't write this question. Ask for another one."
      )}
    </p>
  </div>
);

/**
 * The generation request a pending draft makes, and the states around it.
 *
 * The rules this holds, in the order they matter:
 *
 * - **Exactly one request per mounted draft.** `startedRef` is what guarantees
 *   it: React 18's StrictMode mounts, unmounts and remounts every component in
 *   development, and a re-render for any other reason must not ask the server to
 *   write a second question. The server answers a concurrent request `202`
 *   rather than generating twice, but the client should not be making that
 *   request at all.
 * - **Nothing lands after unmount.** `cancelledRef` is re-armed on mount and set
 *   by the cleanup, and the poll's timer is cleared there too, so a student who
 *   scrolls a thread away from a slow draft leaves nothing running.
 * - **The failed state is the only end other than a question.** A timeout, a
 *   refused request, a payload this client cannot parse: all of them read
 *   "Ora couldn't write this question", because none of them is something the
 *   student can act on differently. There is no retry button in v1.
 */
const DraftQuestionGate = ({
  payload,
  isReadOnly,
  onBeforeContentExpand,
}: {
  payload: ChatDraftQuestionBlockPayload;
  isReadOnly: boolean;
  onBeforeContentExpand?: () => void;
}) => {
  const [current, setCurrent] =
    useState<ChatDraftQuestionBlockPayload>(payload);

  const startedRef = useRef(false);
  const cancelledRef = useRef(false);
  const settledRef = useRef(payload.status !== 'pending');
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { draftId } = payload;
  // A hidden block is mounted behind a reveal chip. Writing a question costs a
  // model call and one of the student's drafts for the day, so it waits until
  // the block is actually on screen; revealing it starts the request.
  const isHidden = useIsChatBlockHidden();

  const shouldGenerate =
    payload.status === 'pending' && !isReadOnly && !isHidden;

  /**
   * The server's own word about this draft outranks whatever this component
   * worked out for itself. A re-hydration (a reload, an anchor change) re-reads
   * the row and hands down the payload for its CURRENT status, so a draft that
   * finished while this block was polling — or after it had given up — arrives
   * here as `ready`, and must replace what is on screen. Only a settled payload
   * does that: an incoming `pending` is what this block is already working on.
   *
   * Set during render, which is how React says to adjust state to new props;
   * `appliedStatus` is what stops it from looping, because the payload object
   * itself is parsed fresh on every render of the thread.
   */
  const [appliedStatus, setAppliedStatus] = useState(payload.status);

  if (payload.status !== appliedStatus) {
    setAppliedStatus(payload.status);

    if (payload.status !== 'pending') setCurrent(payload);
  }

  // Whatever settled it — this block's own request, or a payload from above —
  // there is nothing left to wait for, and the poll stops.
  useEffect(() => {
    settledRef.current = current.status !== 'pending';

    if (settledRef.current && pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [current.status]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!shouldGenerate || startedRef.current) {
      return () => {
        cancelledRef.current = true;
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      };
    }

    startedRef.current = true;

    /** What the server sent, if this client can draw it. */
    const parse = (raw: Record<string, unknown>) => {
      const parsed = parseDraftQuestionBlockPayload(raw);
      return parsed && parsed.draftId === draftId ? parsed : null;
    };

    /** Nothing more to do: unmounted, or the draft already settled. */
    const isDone = () => cancelledRef.current || settledRef.current;

    /** The question (or the failure) replaces the waiting line, in place. */
    const settle = (next: ChatDraftQuestionBlockPayload) => {
      if (isDone()) return;

      // The block is about to grow by a whole question; the thread keeps the
      // student's position across that, as it does for a revealed explanation.
      onBeforeContentExpand?.();
      setCurrent(next);
    };

    const fail = () => {
      if (isDone()) return;

      settle({
        draftId,
        status: 'failed',
        sourceLabel: payload.sourceLabel,
        reason: 'timed_out',
      });
    };

    /** Ask again until the row leaves pending, or the window closes. */
    const poll = async (deadline: number) => {
      if (isDone()) return;

      if (Date.now() >= deadline) {
        fail();
        return;
      }

      const result = await fetchChatDraftQuestion(draftId);
      if (isDone()) return;

      if (result.kind === 'payload') {
        const parsed = parse(result.payload);

        if (parsed && parsed.status !== 'pending') {
          settle(parsed);
          return;
        }
      }

      // Still being written, or a blip on one read: ask again. Only the window
      // ends the wait.
      pollTimerRef.current = setTimeout(
        () => void poll(deadline),
        CHAT_DRAFT_POLL_INTERVAL_MS
      );
    };

    const startPolling = () => {
      const deadline = Date.now() + CHAT_DRAFT_POLL_WINDOW_MS;

      pollTimerRef.current = setTimeout(
        () => void poll(deadline),
        CHAT_DRAFT_POLL_INTERVAL_MS
      );
    };

    const run = async () => {
      const result = await generateChatDraftQuestion(draftId);
      if (isDone()) return;

      if (result.kind === 'error') {
        fail();
        return;
      }

      if (result.kind === 'payload') {
        const parsed = parse(result.payload);

        // The generation request is the authoritative answer: a body this
        // client cannot read is as final as a refusal, and polling for a
        // better one would only make the student wait two more minutes.
        if (!parsed) {
          fail();
          return;
        }

        if (parsed.status !== 'pending') {
          settle(parsed);
          return;
        }
      }

      // A 202 (someone else's request is writing it), or a 200 that still says
      // pending: both are "not yet", and both are waited out the same way.
      startPolling();
    };

    void run();

    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [draftId, onBeforeContentExpand, payload.sourceLabel, shouldGenerate]);

  if (current.status === 'ready') {
    return (
      <ReadyDraftQuestion
        payload={current}
        isReadOnly={isReadOnly}
        onBeforeContentExpand={onBeforeContentExpand}
      />
    );
  }

  return (
    <DraftStatusView
      isPending={current.status === 'pending'}
      sourceLabel={current.sourceLabel}
    />
  );
};

/**
 * The block the registry draws for a `draft_question`, in whichever of its
 * three states the server sent. Keyed on the draft so a thread that replaces
 * one draft with another starts the new one's wait from scratch.
 */
const DraftQuestionBlock = ({
  payload,
  isReadOnly,
  onBeforeContentExpand,
}: {
  payload: ChatDraftQuestionBlockPayload;
  isReadOnly: boolean;
  onBeforeContentExpand?: () => void;
}) => (
  <DraftQuestionGate
    key={payload.draftId}
    payload={payload}
    isReadOnly={isReadOnly}
    onBeforeContentExpand={onBeforeContentExpand}
  />
);

export default DraftQuestionBlock;
